import { forwardRef, useEffect } from "react";

import { observer } from "mobx-react-lite";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { preload } from "react-dom";

import { footerLinks, socialLinks } from "@/components/doc/left-sidebar/SidebarFooter";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { twMerge } from "@/utils/twMerge";

import type { DropdownItem } from "@/components/sidebar/Dropdown";
import type { ComponentProps } from "react";

type LinkType = (typeof footerLinks)[number] | (typeof socialLinks)[number];

type LinkItemProps = {
  link: LinkType;
  close: () => void;
  withBorderTop?: boolean;
};

const LinkItem = forwardRef<HTMLAnchorElement, LinkItemProps & ComponentProps<"button">>(
  ({ link, close, withBorderTop = false, ...props }: LinkItemProps, ref) => {
    const t = useTranslations();

    const style = twMerge(
      "w-full h-8.5 px-2.5 text-sm flex items-center gap-2 rounded-8 whitespace-nowrap tracking-tight sm:text-[16px]",
      "data-[focus]:bg-sidebar-button-active-bg",
      withBorderTop &&
        "relative mt-[20px] before:pointer-events-none before:absolute before:top-[-10px] before:left-[5px] before:right-[5px] before:h-[1px] before:bg-gray-200 dark:before:bg-gray-800",
    );
    const { theme, systemTheme } = useTheme();

    const getIconSrc = () => {
      if (!("icon" in link)) {
        return undefined;
      }
      if (link.iconDark && (theme === "dark" || (theme === "system" && systemTheme === "dark"))) {
        return link.iconDark;
      }
      return link.icon;
    };
    const iconSrc = getIconSrc();

    const icon = iconSrc ? (
      <img src={iconSrc} alt="" width={24} height={24} className="ml-auto" />
    ) : null;

    if (link.href.startsWith("https://")) {
      return (
        <a
          {...props}
          href={link.href}
          target="_blank"
          rel="noreferrer"
          onClick={close}
          ref={ref}
          className={style}
        >
          {t(link.intlName)}
          {icon}
        </a>
      );
    }

    return (
      <Link {...props} href={link.href} onClick={close} ref={ref} className={style}>
        {t(link.intlName)}
        {icon}
      </Link>
    );
  },
);

export const LinksButton = observer(function LinksButton() {
  const t = useTranslations();

  useEffect(() => {
    socialLinks.forEach((link) => {
      preload(link.icon, { as: "image" });
      if (link.iconDark) {
        preload(link.iconDark, { as: "image" });
      }
    });
  }, []);

  const footerItems: DropdownItem[] = footerLinks.map((link) => ({
    title: t(link.intlName),
    component: ({ close }) => <LinkItem link={link} close={close} />,
  }));

  const socialItems: DropdownItem[] = socialLinks.map((link, index) => ({
    title: t(link.intlName),
    component: ({ close }) => <LinkItem link={link} close={close} withBorderTop={index === 0} />,
  }));

  const items: DropdownItem[] = [...footerItems, ...socialItems];

  const copyrightText = t("sidebarFooter.copyright", { year: new Date().getFullYear() });
  items.push({
    title: copyrightText,
    component: () => (
      <p
        className={twMerge(
          "flex items-center w-full h-8.5 px-2.5 text-sm text-secondary tracking-tight",
          "sm:text-[16px]",
        )}
      >
        {copyrightText}
      </p>
    ),
  });

  return (
    <Dropdown
      toggleButtonComponent={
        <Sidebar.Button as="div" aria-label={t("sidebar.showLinks")}>
          <Icon name="More" />
        </Sidebar.Button>
      }
      items={items}
    />
  );
});
